import Brand from 'data/Brand';
import { navigate } from 'hooks/useHookRouterCompat';
import { Size } from 'lib/styles';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Background, Border, Main } from 'styles/themeColors';
import placeholderImage from 'images/placeholder-image.jpg';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import BrandType from './BrandTypeChip';
import FeatureFlag from 'data/FeatureFlag';
import { BRAND_TYPE } from 'constants/brands';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';
import { KeyboardArrowUp as KeyboardArrowUpIcon, KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';

const PARENT_BRAND_CARD_LABELS = {
  LOGO_ALT: (name: string) => `${name} logo`,
  PLACEHOLDER_ALT: (name: string) => `${name} placeholder-logo`,
};

const ParentCardContainer = styled.div`
  border: 1px solid ${Border.brand};
  background: ${Background.lightBrand};
  border-radius: 6px;
  overflow: hidden;
  display: inline-block;
  width: 100%;
`;

const ParentHeader = styled.div<{ isExpanded: boolean }>`
  padding: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  min-height: 64px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${Main.brandLighter};
  }
`;

const Label = styled.div`
  font-size: 24px;
`;

const BrandImage = styled.img`
  height: 40px;
  margin-right: ${Size.medium};
  object-fit: contain;
  object-position: 50%;
  width: 40px;
`;

const TagWithArrow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Tag = styled.div`
  align-self: flex-start;
`;

const ArrowIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Main.brandDarkest};
`;

const ChildrenContainer = styled.div<{ isExpanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ isExpanded }) => (isExpanded ? '1fr' : '0fr')};
  overflow: hidden;
  transition: grid-template-rows 0.3s ease-in-out, opacity 0.2s ease-in-out;
  opacity: ${({ isExpanded }) => (isExpanded ? '1' : '0')};
`;

const ChildrenWrapper = styled.div<{ isExpanded: boolean }>`
  min-height: 0;
  padding: ${({ isExpanded }) => (isExpanded ? '0 16px 16px 16px' : '0 16px 0 16px')};
  display: flex;
  flex-direction: column;
  gap: ${({ isExpanded }) => (isExpanded ? '8px' : '0')};
  overflow: hidden;
  transition: padding 0.3s ease-in-out, gap 0.3s ease-in-out;
`;

const ChildCard = styled.div`
  border: 1px solid ${Border.main};
  padding: 10px;
  background: ${Background.white};
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 6px;
  min-height: 56px;
  justify-content: space-between;
  cursor: pointer;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    border-color: ${Main.brandDark};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ChildBrandImage = styled.img`
  height: 32px;
  margin-right: ${Size.small};
  object-fit: contain;
  object-position: 50%;
  width: 32px;
`;

const ChildLabel = styled.div`
  font-size: 18px;
`;

interface Props {
  parentBrand: Brand;
  childBrands: Brand[];
  featureFlags?: FeatureFlag;
  readOnly: boolean;
}

const ParentBrandCard: React.FC<Props> = ({ parentBrand, childBrands, readOnly }: Props) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = childBrands.length > 0;

  const handleParentClick = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleParentNavigate = () => {
    if (!readOnly) {
      navigate(`/brands/brand-details/${parentBrand.id}`);
    }
  };

  const handleChildClick = (childId: number) => {
    if (!readOnly) {
      navigate(`/brands/brand-details/${childId}`);
    }
  };

  return (
    <ReadOnlyWrapper readOnly={readOnly}>
      <ParentCardContainer>
        <ParentHeader isExpanded={isExpanded} onClick={handleParentClick}>
          <FlexboxContainerComponent onClick={handleParentNavigate}>
            {parentBrand.media && parentBrand.media.length > 0 ? (
              <BrandImage
                alt={PARENT_BRAND_CARD_LABELS.LOGO_ALT(parentBrand.name)}
                src={process.env.REACT_APP_MEDIA_URL + parentBrand.media[0].path}
              />
            ) : (
              <BrandImage
                alt={PARENT_BRAND_CARD_LABELS.PLACEHOLDER_ALT(parentBrand.name)}
                src={placeholderImage}
              />
            )}
            <Label>{parentBrand.name}</Label>
          </FlexboxContainerComponent>

          <TagWithArrow>
            <Tag>
              <BrandType type={BRAND_TYPE.PARENT_BRAND} />
            </Tag>
            {hasChildren && (
              <ArrowIcon>
                {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </ArrowIcon>
            )}
          </TagWithArrow>
        </ParentHeader>

        {hasChildren && (
          <ChildrenContainer isExpanded={isExpanded}>
            <ChildrenWrapper isExpanded={isExpanded}>
              {childBrands.map((child) => (
                <ChildCard
                  key={child.id}
                  onClick={readOnly ? undefined : () => handleChildClick(child.id)}
                >
                  <FlexboxContainerComponent>
                    {child.media && child.media.length > 0 ? (
                      <ChildBrandImage
                        alt={PARENT_BRAND_CARD_LABELS.LOGO_ALT(child.name)}
                        src={process.env.REACT_APP_MEDIA_URL + child.media[0].path}
                      />
                    ) : (
                      <ChildBrandImage
                        alt={PARENT_BRAND_CARD_LABELS.PLACEHOLDER_ALT(child.name)}
                        src={placeholderImage}
                      />
                    )}
                    <ChildLabel>{child.name}</ChildLabel>
                  </FlexboxContainerComponent>

                  <Tag>
                    <BrandType type={BRAND_TYPE.SUB_BRAND} />
                  </Tag>
                </ChildCard>
              ))}
            </ChildrenWrapper>
          </ChildrenContainer>
        )}
      </ParentCardContainer>
    </ReadOnlyWrapper>
  );
};

export default React.memo(ParentBrandCard);
